import { useAuth } from '@/auth';
import { checkAllPermissions, PERMISSIONS } from '@/auth/permissions';
import { formatDateLocale, formatDateTimeLocale } from '@/common/utils/date';
import { TRAMITE_LABELS } from '@/common/constants';
import { useHasAnyIndicatorPermission } from '@/hooks/useHasAnyIndicatorPermission';
import {
  TramiteStatusDialog,
  useTramiteStatusDialog
} from '@/components/TramiteStatus';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { getVencimientoPresentacion } from '@/hooks/useVencimientoPresentacion';
import { ArrowUpDown, ChevronDown, ChevronUp, ArrowRightFromLine as VerTramiteIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QuickAssignUserDialog from '@/components/QuickAssignUserDialog';
import type { UserOption } from '@/otros-tramites/services/otrosTramitesService';
import TableCellActions from './TableCellActions';
import TramiteIndicators from './TramiteIndicators';
import TramiteBudgetIndicators from './TramiteBudgetIndicators';
import { BloqueadaBadge } from './BloqueadaBadge';
import { ArchivadoBadge } from './ArchivadoBadge';
import { TramiteStatusCell } from '@/components/TramiteStatus';
import {
  TareasProgressCell,
  TramiteTareasDialog,
  useTramiteTareasDialog,
} from '@/tramite-tareas';


type SortField = 'id' | 'vendedor' | 'comprador' | 'fecini' | 'diasDesdeInicio' | 'vigReserva' | 'vencimientoPresentacion' | 'fecfirma' | 'fecescritura' | 'num_escritura' | 'estado' | 'tramites' | 'numregistro';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

interface TramitesTableProps {
  tramites: any[];
  loading: boolean;
  handleClickFinalizar: (id: number) => () => void;
  onClickBloqueo?: (tramite: any) => void;
  onClickArchivado?: (tramite: any) => void;
  currentPage: number;
  /** Con el modo selección activo aparece la columna de checkboxes. */
  selectionMode?: boolean;
  selectedIds?: Set<number>;
  onToggleSelect?: (id: number) => void;
  onToggleAll?: () => void;
  onSortChanged?: (field: SortField, direction: SortDirection) => void;
  sortConfig?: SortConfig;
  onAssignedUserSave?: (tramite: any, assignedUser: UserOption | null) => Promise<void>;
  /** Se llama tras una mutación del checklist de tareas, para refrescar los
   * contadores de la grilla (ver `TareasProgressCell`). */
  onTareasChanged?: () => void;
}

interface LegacyAssignedUser {
  id: number;
  name: string;
  email?: string;
}

const getAssignedUserOption = (assignedUsers: LegacyAssignedUser[] | undefined): UserOption | null => {
  const firstAssignedUser = assignedUsers?.[0];
  if (!firstAssignedUser) {
    return null;
  }

  return {
    id: String(firstAssignedUser.id),
    nombre: firstAssignedUser.name,
    email: firstAssignedUser.email,
    userId: String(firstAssignedUser.id),
    source: 'user',
  };
};

const TramitesTable = ({
  tramites,
  loading,
  handleClickFinalizar,
  onClickBloqueo,
  onClickArchivado,
  currentPage,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  onSortChanged,
  sortConfig,
  onAssignedUserSave,
  onTareasChanged,
}: TramitesTableProps) => {
  const { user } = useAuth();
  const userPermissions = user?.permissions || [];
  const canManageTramites = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITES.name]);
  const canSeeAnyIndicator = useHasAnyIndicatorPermission();
  const { isColumnVisible } = useColumnVisibility();
  const { isOpen, selectedTramite, openDialog, closeDialog } = useTramiteStatusDialog();
  const {
    isOpen: isTareasOpen,
    selectedTramite: selectedTareasTramite,
    openDialog: openTareasDialog,
    closeDialog: closeTareasDialog,
  } = useTramiteTareasDialog();
  const navigate = useNavigate();
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const [assignmentDialogState, setAssignmentDialogState] = useState<any | null>(null);
  const [isSavingAssignedUser, setIsSavingAssignedUser] = useState(false);

  // Determine the first visible column for placing the floating action button
  const getFirstVisibleColumn = (): string | null => {
    const columnOrder = [
      'id',
      'numregistro',
      'vendedor',
      'comprador',
      'asignados',
      'fecini',
      'fecfirma',
      'fecescritura',
      'num_escritura',
      'vencimientoPresentacion',
      'tramites',
      'tareas',
      'bloqueada',
      'archivada',
      'indicadores',
      'acciones'
    ];

    for (const col of columnOrder) {
      // Special handling for columns that require additional permissions
      if ((col === 'tramites' || col === 'indicadores') && !canSeeAnyIndicator) {
        continue;
      }
      if (isColumnVisible(col)) {
        return col;
      }
    }
    return null;
  };

  const firstVisibleColumn = getFirstVisibleColumn();

  const handleSort = (field: SortField) => {
    if (!onSortChanged) return;

    const direction = sortConfig?.field === field && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
    onSortChanged(field, direction);
  };

  const handleViewTramite = (tramite: any) => {
    navigate('/dashboard', { state: { tramite } });
  };

  const getSortIcon = (field: SortField) => {
    if (!sortConfig || sortConfig.field !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }

    return sortConfig.direction === 'asc'
      ? <ChevronUp className="h-4 w-4 ml-1 text-gray-600" />
      : <ChevronDown className="h-4 w-4 ml-1 text-gray-600" />;
  };

  // Floating action button component
  const FloatingActionButton = ({ tramite }: { tramite: any }) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.button
          initial={{ x: -40, opacity: 0, scale: 0.8 }}
          animate={{ x: -10, opacity: 1, scale: 1 }}
          exit={{ x: -40, opacity: 0, scale: 0.8 }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 25,
            mass: 0.8
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleViewTramite(tramite);
          }}
          className="absolute left-0 top-1/2 -translate-y-1/2 bg-primary hover:bg-primary/90 text-white rounded-full p-1.5 shadow-lg hover:shadow-xl transition-shadow z-[9999]"
          aria-label="Ver trámite"
        >
          <VerTramiteIcon className="h-4 w-4" />
        </motion.button>
      </TooltipTrigger>
      <TooltipContent side="left">
        Ver trámite
      </TooltipContent>
    </Tooltip>
  );

  // Helper to check if floating button should be rendered in this column
  const shouldShowFloatingButton = (columnName: string, tramiteId: number) => {
    // En modo selección el checkbox ocupa la primera columna y el botón
    // flotante se le encimaba; además ahí el usuario está seleccionando, no
    // navegando.
    if (selectionMode) return false;
    return hoveredRowId === tramiteId && firstVisibleColumn === columnName;
  };

  const seleccionadas = selectedIds ?? new Set<number>();
  const idsVisibles = (tramites ?? []).map((tramite) => tramite.id);
  const todasSeleccionadas = idsVisibles.length > 0 && idsVisibles.every((id) => seleccionadas.has(id));
  const algunaSeleccionada = idsVisibles.some((id) => seleccionadas.has(id));

  const SortableHeader = ({ field, children, className = '' }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <TableHead
      className={`cursor-pointer hover:bg-gray-50 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center justify-between">
        <span>{children}</span>
        {getSortIcon(field)}
      </div>
    </TableHead>
  );

  const handleOpenAssignmentDialog = (tramite: any) => {
    setAssignmentDialogState(tramite);
  };

  const handleSaveAssignedUser = async (assignedUser: UserOption | null) => {
    if (!assignmentDialogState || !onAssignedUserSave) {
      return;
    }

    try {
      setIsSavingAssignedUser(true);
      await onAssignedUserSave(assignmentDialogState, assignedUser);
      setAssignmentDialogState(null);
    } finally {
      setIsSavingAssignedUser(false);
    }
  };

  return (
    <div>
      <TramiteStatusDialog
        tramite={selectedTramite}
        isOpen={isOpen}
        onClose={closeDialog}
      />
      <TramiteTareasDialog
        tramite={selectedTareasTramite}
        isOpen={isTareasOpen}
        onClose={closeTareasDialog}
        onChanged={onTareasChanged}
      />
      {assignmentDialogState && (
        <QuickAssignUserDialog
          open={Boolean(assignmentDialogState)}
          onOpenChange={(open) => {
            if (!open) {
              setAssignmentDialogState(null);
            }
          }}
          entityLabel={`la escritura #${assignmentDialogState.id}`}
          initialAssignedUser={getAssignedUserOption(assignmentDialogState.assigned_users)}
          isSubmitting={isSavingAssignedUser}
          onSubmit={handleSaveAssignedUser}
        />
      )}
      <div className="relative overflow-visible">
        <Table>
          <TableHeader>
            <TableRow>
              {selectionMode && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={todasSeleccionadas ? true : algunaSeleccionada ? 'indeterminate' : false}
                    onCheckedChange={() => onToggleAll?.()}
                    aria-label="Seleccionar todas las escrituras de la página"
                  />
                </TableHead>
              )}
              {isColumnVisible('id') && <SortableHeader field="id" className='max-w-[40px]'>#</SortableHeader>}
              {isColumnVisible('numregistro') && <SortableHeader field="numregistro"># Registro</SortableHeader>}
              {isColumnVisible('vendedor') && <SortableHeader field="vendedor">{TRAMITE_LABELS.TRANSFERENTE}</SortableHeader>}
              {isColumnVisible('comprador') && <SortableHeader field="comprador">{TRAMITE_LABELS.ADQUIRIENTE}</SortableHeader>}
              {isColumnVisible('asignados') && <TableHead>Asignados</TableHead>}
              {isColumnVisible('fecini') && <SortableHeader field="fecini">Fecha de inicio</SortableHeader>}
              {isColumnVisible('fecfirma') && <SortableHeader field="fecfirma">Fecha Est. Firma:</SortableHeader>}
              {isColumnVisible('fecescritura') && <SortableHeader field="fecescritura">Fecha de escritura</SortableHeader>}
              {isColumnVisible('num_escritura') && <SortableHeader field="num_escritura"># Escritura</SortableHeader>}
              {isColumnVisible('vencimientoPresentacion') && <SortableHeader
                field="vencimientoPresentacion"
                className='max-w-[100px] overflow-hidden text-ellipsis whitespace-normal'>
                Vencimiento presentación
              </SortableHeader>}
              {canSeeAnyIndicator && isColumnVisible('tramites') && (
                <SortableHeader field="tramites">Trámites</SortableHeader>
              )}
              {isColumnVisible('tareas') && <TableHead>Tareas</TableHead>}
              {isColumnVisible('bloqueada') && <TableHead>Bloqueada</TableHead>}
              {isColumnVisible('archivada') && <TableHead>Archivada</TableHead>}
              {canSeeAnyIndicator && isColumnVisible('indicadores') && (
                <SortableHeader field="estado">Indicadores</SortableHeader>
              )}
              {isColumnVisible('acciones') && <TableHead>Acciones</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Skeleton loading rows
              Array.from({ length: 6 }).map((_, index) => {
                const visibleColumnCount = [
                  isColumnVisible('id'),
                  isColumnVisible('numregistro'),
                  isColumnVisible('vendedor'),
                  isColumnVisible('comprador'),
                  isColumnVisible('asignados'),
                  isColumnVisible('fecini'),
                  isColumnVisible('fecfirma'),
                  isColumnVisible('fecescritura'),
                  isColumnVisible('num_escritura'),
                  isColumnVisible('vencimientoPresentacion'),
                  canSeeAnyIndicator && isColumnVisible('tramites'),
                  isColumnVisible('tareas'),
                  isColumnVisible('bloqueada'),
                  isColumnVisible('archivada'),
                  canSeeAnyIndicator && isColumnVisible('indicadores'),
                  isColumnVisible('acciones'),
                  selectionMode
                ].filter(Boolean).length;

                return (
                  <TableRow key={`skeleton-${index}`}>
                    {Array.from({ length: visibleColumnCount }).map((_, colIndex) => (
                      <TableCell key={`skeleton-cell-${index}-${colIndex}`}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              tramites && tramites.map((tramite, index) => {
                const { vencimientoPresentacion, diasEscritura } = getVencimientoPresentacion(tramite);

                // Determine row background color based on vencimientoPresentacion days
                let rowClassName = '';
                if (canManageTramites && isColumnVisible('vencimientoPresentacion') && tramite.fecescritura && diasEscritura <= 10 && diasEscritura > 0) {
                  rowClassName = 'bg-orange-100 hover:bg-orange-200';
                } else {
                  rowClassName = index % 2 === 0 ? 'bg-gray-100' : '';
                }

                if (seleccionadas.has(tramite.id)) {
                  rowClassName = 'bg-blue-50 hover:bg-blue-100';
                }

                return (
                  <TableRow
                    key={`tramite-${tramite.id}`}
                    className={`${rowClassName} relative`}
                    onMouseEnter={() => setHoveredRowId(tramite.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                  >
                    {selectionMode && (
                      <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={seleccionadas.has(tramite.id)}
                          onCheckedChange={() => onToggleSelect?.(tramite.id)}
                          aria-label={`Seleccionar escritura ${tramite.id}`}
                        />
                      </TableCell>
                    )}
                    {isColumnVisible('id') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('id', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        <span className="relative z-0">{tramite.id}</span>
                      </TableCell>
                    )}
                    {isColumnVisible('numregistro') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('numregistro', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        {tramite.numregistro || '-'}
                      </TableCell>
                    )}
                    {isColumnVisible('vendedor') && (
                      <TableCell className="max-w-[150px] relative overflow-visible">
                        {shouldShowFloatingButton('vendedor', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        {tramite.vendedores?.[0]?.nombre ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="max-w-[150px] truncate cursor-default">
                                {tramite.vendedores[0].nombre}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {tramite.vendedores[0].nombre}
                            </TooltipContent>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                    )}
                    {isColumnVisible('comprador') && (
                      <TableCell className="max-w-[150px] relative overflow-visible">
                        {shouldShowFloatingButton('comprador', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        {tramite.compradores?.[0]?.nombre ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="max-w-[150px] truncate cursor-default">
                                {tramite.compradores[0].nombre}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {tramite.compradores[0].nombre}
                            </TooltipContent>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                    )}
                    {isColumnVisible('asignados') && (
                      <TableCell className="max-w-[170px] relative overflow-visible">
                        {shouldShowFloatingButton('asignados', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        {(() => {
                          const assignedUsers = Array.isArray(tramite.assigned_users) ? tramite.assigned_users as LegacyAssignedUser[] : [];
                          const assignedUsersLabel = assignedUsers.length > 0
                            ? assignedUsers.map((user) => user.name).join(', ')
                            : 'Asignar';

                          if (!canManageTramites || !onAssignedUserSave) {
                            return assignedUsers.length > 0 ? assignedUsersLabel : '-';
                          }

                          return (
                            <button
                              type="button"
                              className="block max-w-[160px] truncate text-left text-primary hover:underline"
                              onClick={() => handleOpenAssignmentDialog(tramite)}
                              title={assignedUsers.length > 0 ? assignedUsersLabel : 'Asignar responsable'}
                            >
                              {assignedUsersLabel}
                            </button>
                          );
                        })()}
                      </TableCell>
                    )}
                    {isColumnVisible('fecini') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('fecini', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        {tramite.fecini ? formatDateLocale(tramite.fecini) : '-'}
                      </TableCell>
                    )}
                    {isColumnVisible('fecfirma') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('fecfirma', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        {tramite.fecfirma ? formatDateTimeLocale(tramite.fecfirma) : '-'}
                      </TableCell>
                    )}
                    {isColumnVisible('fecescritura') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('fecescritura', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        {tramite.fecescritura ? formatDateLocale(tramite.fecescritura) : '-'}
                      </TableCell>
                    )}
                    {isColumnVisible('num_escritura') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('num_escritura', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        {tramite.num_escritura || '-'}
                      </TableCell>
                    )}
                    {isColumnVisible('vencimientoPresentacion') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('vencimientoPresentacion', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        {vencimientoPresentacion}
                      </TableCell>
                    )}
                    {canSeeAnyIndicator && isColumnVisible('tramites') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('tramites', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        <TramiteStatusCell
                          tramite={tramite}
                          clickable={canManageTramites}
                          onClick={openDialog}
                          showProgress={canManageTramites}
                        />
                      </TableCell>
                    )}
                    {isColumnVisible('tareas') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('tareas', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        <TareasProgressCell
                          tramite={tramite}
                          clickable
                          onClick={openTareasDialog}
                        />
                      </TableCell>
                    )}
                    {isColumnVisible('bloqueada') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('bloqueada', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        <BloqueadaBadge tramite={tramite} />
                      </TableCell>
                    )}
                    {isColumnVisible('archivada') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('archivada', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        <ArchivadoBadge tramite={tramite} />
                      </TableCell>
                    )}
                    {canSeeAnyIndicator && isColumnVisible('indicadores') && (
                      <TableCell className="relative overflow-visible">
                        {shouldShowFloatingButton('indicadores', tramite.id) && <FloatingActionButton tramite={tramite} />}
                        <TramiteBudgetIndicators tramite={tramite} />
                      </TableCell>
                    )}
                    {isColumnVisible('acciones') && <TableCellActions
                      tramite={tramite}
                      onClickFinalizar={handleClickFinalizar}
                      onClickBloqueo={onClickBloqueo}
                      onClickArchivado={onClickArchivado}
                    />}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
      <div className="px-6 py-2 mt-4 text-sm text-muted-foreground text-center font-medium">
        {`Página actual: ${currentPage || ''} | Registros: ${tramites?.length} `}
      </div>
    </div>
  )
}

export default TramitesTable;
